import { darken, lighten, rgba } from 'polished';
import styled from '@emotion/styled/macro';
import FormLabel from '@mui/material/FormLabel';
import {
  space,
  layout,
  typography,
  color,
  position,
  compose,
  border,
  shadow,
  flexbox,
  background,
  grid,
} from 'styled-system';
import { Link as RouterLink } from 'react-router-dom';
import { keyframes } from '@emotion/react';

const ignoreStyle = [
  'marginTop',
  'marginRight',
  'marginBottom',
  'marginLeft',
  'marginX',
  'marginY',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'paddingX',
  'paddingY',
  'backgroundColor',
  'fontSize',
  'fontFamily',
  'fontStyle',
  'textAlign',
  'lineHeight',
  'fontWeight',
  'letterSpacing',
  'maxWidth',
  'minWidth',
  'maxHeight',
  'minHeight',
  'verticalAlign',
  'overflowX',
  'overflowY',
  'alignItems',
  'justifyItems',
  'alignContent',
  'justifyContent',
  'flexWrap',
  'flexShrink',
  'flexGrow',
  'flexBasis',
  'flexDirection',
  'justifySelf',
  'alignSelf',
  'gridGap',
  'gridColumnGap',
  'gridRowGap',
  'gridColumn',
  'gridRow',
  'gridAutoFlow',
  'gridAutoColumns',
  'gridAutoRows',
  'gridTemplateColumns',
  'gridTemplateRows',
  'gridTemplateAreas',
  'gridArea',
  'backgroundImage',
  'backgroundSize',
  'backgroundPosition',
  'backgroundRepeat',
  'borderWidth',
  'borderStyle',
  'borderColor',
  'borderRadius',
  'borderTop',
  'borderTopWidth',
  'borderTopStyle',
  'borderTopColor',
  'borderTopLeftRadius',
  'borderTopRightRadius',
  'borderRight',
  'borderRightWidth',
  'borderRightStyle',
  'borderRightColor',
  'borderBottom',
  'borderBottomWidth',
  'borderBottomStyle',
  'borderBottomColor',
  'borderBottomLeftRadius',
  'borderBottomRightRadius',
  'borderLeft',
  'borderLeftWidth',
  'borderLeftStyle',
  'borderLeftColor',
  'borderX',
  'borderY',
  'zIndex',
  'textShadow',
  'boxShadow',
  'isActive',
  'btnType',
  'isCircle',
  'hasAnimation',
  'hasActiveOverlay',
  'bgColor',
  'isCard',
];

export const Compose = compose(
  space,
  grid,
  color,
  layout,
  border,
  position,
  shadow,
  flexbox,
  typography,
  background,
);

// export const commonStyle = (component, style = '') => styled(
//   component,
// ).withConfig({
//   shouldForwardProp: prop => !ignoreStyle.includes(prop),
// })`
//   && {
//     ${style}
//     ${Compose}
//   }
// `;

export const commonStyle = (component, style = '') => styled(component, {
  shouldForwardProp: props => !ignoreStyle.includes(String(props)),
})`
  && {
    ${style}
    ${Compose}
  }
`;

/* Tags */
// Add styled-system functions to your component
export const Div = styled.div`
  ${({ theme, gap }) => (gap ? `gap: ${theme.space[gap]}px` : '')};
  ${Compose}

  ${({ hasScroll, theme }) =>
    hasScroll
      ? `
    ::-webkit-scrollbar {
      ${theme.mediaQueries.md} {
        height: 4px;
        width: 0px;
      }
    }
    `
      : ''}
`;

export const Span = styled.span`
  ${Compose}
`;

export const H1 = styled.h1`
  ${Compose}
`;

export const H2 = styled.h2`
  ${Compose}
`;

export const H3 = styled.h3`
  ${Compose}
`;

export const H4 = styled.h4`
  ${Compose}
`;

export const H5 = styled.h5`
  ${Compose}
`;

export const H6 = styled.h6`
  ${Compose}
`;

export const Link = styled(RouterLink)`
  ${Compose}
`;

export const TextView = styled.div`
  line-height: 2.5;
  color: inherit;
  ${Compose}
`;

export const Tag = styled.span`
  display: inline-flex;
  justify-content: center;
  gap: 4px;
  padding: 2px 8px;
  height: auto;
  white-space: nowrap;
  border-radius: ${({ theme }) => theme.radii.high};
  font-size: ${({ theme }) => theme.fontSizes[0]};
  background-color: ${({ theme, variant }) =>
    rgba(theme.colors[variant] || theme.colors.tertiaryColor, 0.15)};
  color: ${({ theme, variant }) =>
    theme.colors[variant] || theme.colors.tertiaryColor};

  ${Compose}
`;

export const Point = styled.span`
  width: 6px;
  height: 6px;
  display: inline-block;
  border-radius: ${({ theme }) => theme.radii.high};
  background-color: ${({ theme }) => theme.colors.redColor};

  ${Compose}
`;

export const Paper = styled.div`
  ${({ theme, gap }) => (gap ? `gap: ${theme.space[gap]}px` : '')};
  background-color: ${({ theme, disabled }) =>
    (theme.mode === 'dark' ? !disabled : disabled)
      ? darken(0.03, theme.colors.primaryColor)
      : lighten(0.1, theme.colors.primaryColor)};
  border-radius: ${({ theme, square }) => (square ? 0 : theme.radii.lg)};

  ${({ theme, elevation, variant }) =>
    variant === 'outline'
      ? `border: ${theme.borders[1]};`
      : `box-shadow: ${theme.shadows[elevation || 25]};`}

  ${Compose}
`;

/* Box */
export const Box = styled(Paper)`
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100px;
  width: 100%;

  ${Compose}
`;

export const OpacityBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: ${({ theme }) => theme.radii.md};
  background-color: ${({ theme }) => rgba(theme.colors.primaryColor, 0.8)};
  color: ${({ theme }) => theme.colors.secondaryColor};
  height: 30px;
  width: max-content;
  padding: 0 12px;
  box-shadow: ${({ theme }) => theme.shadows[25]};
  ${Compose}

  i {
    color: inherit;
  }
`;

export const StickyBottomBox = styled.div`
  background-color: ${({ theme }) => lighten(0.1, theme.colors.primaryColor)};
  box-shadow: ${({ theme }) => theme.shadows[1]};
  padding: 12px;
  position: sticky;
  bottom: 0;
  margin-right: -20px;
  margin-left: -20px;
  z-index: 200;
`;

export const FixedBox = styled.div`
  ${({ theme, gap }) => (gap ? `gap: ${theme.space[gap]}px` : '')};
  display: flex;
  position: absolute;
  margin-top: 12px;
  margin-bottom: 12px;
  right: 16px;
  left: 16px;
  z-index: 1000000;

  ${Compose}
`;

/* Form */
export const StyledFormLabel = commonStyle(FormLabel);
export const Input = commonStyle(
  'input',
  ({ theme }) => `
  border: 0 none;
  border-bottom: ${theme.borders[1]};
  background: transparent;
`,
);

export const Form = styled.form`
  margin-top: 5px;
  margin-bottom: 5px;
  width: 100%;
  border-radius: 8px;

  .MuiAutocomplete-root,
  .MuiFormControl-root {
    margin-bottom: 24px;
    .MuiFormControl-root {
      margin: 0;
    }
  }

  ${({ side }) =>
    side &&
    `
      display: flex;
      align-items: center;
    `}

  ${Compose}
`;

export const FormField = styled.div`
  margin-bottom: 24px;
  ${Compose}
`;

export const Puller = styled.div`
  width: 30px;
  height: 4px;
  background-color: ${({ theme }) => darken(0.15, theme.colors.primaryColor)};
  border-radius: 3px;
  position: absolute;
  top: 10px;
  left: calc(50% - 15px);
`;

export const shapeOverlay = ({ scale, toScale, noAnime }) => `
    position: relative;
    * {
      z-index: 2;
    }

    &:active:before {
      transform: scale(${toScale || 1.8});
    }

    &:before {
      content: '';
      display: block;
      border-radius: inherit;
      background-color: inherit;
      position: absolute;
      height: 100%;
      width: 100%;
      z-index: 1;
      opacity: 0.5;
      transform: scale(${scale || 1});
      ${
        !noAnime
          ? 'animation: anime 1s ease infinite;'
          : 'transition: all 0.5s ease;'
      }
      
      @keyframes anime {
        to {
          transform: scale(${toScale || 1.8});
          opacity: 0;
        }
      }
    }
  `;

const fadeIn = keyframes`
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
`;

export const FadeIn = styled.div`
  @media (prefers-reduced-motion: no-preference) {
    animation-name: ${fadeIn};
    animation-fill-mode: backwards;
    animation-duration: ${({ duration = 600 }) => `${duration}ms`};
    animation-delay: ${({ delay = 0 }) => `${delay}ms`};
  }

  ${Compose}
`;
